<template>
    <div
        v-if="resources"
        class="page__content campsite-calendar"
    >
        <template v-show="calendarLoaded">
            <CalendarFilters
                v-model="filterExtras"
                class="campsite-calendar__filter-wrapper"
                :resources="resources"
                :extras="resourceExtras"
                @collapse-all="collapseAll"
                @filter-only-active="filterOnlyActive"
                @file-uploading="fileUploading"
                @file-uploaded="fileUploaded"
                @bulk-complete="updateData"
                @excel-downloading="excelDownloadStarted"
                @excel-downloaded="excelDownloadFinished"
            />

            <h2 v-if="excelIsDownloading"><i class="fa fa-circle-o-notch fa-spin"></i> {{ $t("dayprice-calendar.excel-downloading")}}</h2>

            <ListCalendar
                v-if="fileDidUpload && !excelIsDownloading"
                ref="calendar"
                :resources="resources"
                :campsites="campsites"
                :filter="filterExtras"
                :only_active="onlyActive"
                @resource-changed="setCampsite"
                @calendar-loaded="calendarLoaded"
            />

            <Modal
                ref="modal"
                :title="modalTitle"
                :submit-button="$t('generic.save')"
                :is-loading="isSubmitPending"
                :on-submit="saveDayPrices"
                :is-button-disabled="isSaveButtonDisabled"
                @closed="resetSelectedCampsite"
            >
                <div class="campsite-calendar__date-picker">
                    <v-date-picker
                        ref="startDate"
                        v-model="selectedDate.start"
                        v-bind="datePickerOptions"
                        is-required
                        @input="setEndDate(selectedDate.start)"
                    >
                        <template #header-title="{ monthLabel, yearLabel }">
                            <a
                                class="campsite-calendar__today-button"
                                @click.stop="setTodayDate('start')"
                            >
                                {{ $t('generic.today') }}
                            </a>
                            <p>{{ `${monthLabel} ${yearLabel}` }}</p>
                        </template>
                        <template v-slot="{ inputValue, showPopover, hidePopover }">
                            <input
                                :value="inputValue"
                                @blur="setEndDate($event, 'start')"
                                @keydown="hideCalendarOnTab($event, hidePopover)"
                                @click="showPopover"
                            />
                        </template>
                    </v-date-picker>

                    <svg
                        class="campsite-calendar__date-picker-icon"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                    </svg>
                    <v-date-picker
                        ref="endDate"
                        v-model="selectedDate.end"
                        v-bind="datePickerOptions"
                        is-required
                    >
                        <template #header-title="{ monthLabel, yearLabel }">
                        <span>
                        <a
                            class="campsite-calendar__today-button"
                            @click.stop="setTodayDate('end')"
                        >
                            {{ $t('generic.today') }}
                        </a>
                        <a
                            class="campsite-calendar__today-button"
                            style="margin-left: 10px"
                            @click.stop="setMaxDate('end')"
                        >
                        {{ $t('generic.maxDate') }}
                    </a>
                    </span>
                            <p>{{ `${monthLabel} ${yearLabel}` }}</p>
                        </template>
                        <template v-slot="{ inputValue, showPopover, hidePopover }">
                            <input
                                :value="inputValue"
                                @blur="setEndDate($event, 'end')"
                                @keydown="hideCalendarOnTab($event, hidePopover)"
                                @click="showPopover"
                            />
                        </template>
                    </v-date-picker>
                </div>
                <div>
                </div>
                <ul>
                    <template v-for="(resource, i) in selectedCampsite">
                        <li
                            v-if="!resource.readonly"
                            :key="resource.id"
                            :id="`resource-${resource.id}`"
                            class="campsite-calendar__form-group"
                            :class="{ 'campsite-calendar__form-group--is-disabled': isInputDisabled(resource) }"
                        >
                            <label
                                class="campsite-calendar__form-label"
                                :for="resource.id"
                            >
                                {{ i === 0 ? 'Preis pro Nacht' : resource.name }}
                            </label>
                            <span class="campsite-calendar__form-input-wrapper">
                                <multiselect
                                    v-if="resource.input_type === 'bool' && isMultiSelect(resource)"
                                    v-model="formData[resource.id]"
                                    :name="resource.id"
                                    class="campsite-calendar__form-input-field"
                                    :options="multiSelectOptions"
                                    track-by="code"
                                    label="label"
                                    :close-on-select="false"
                                    :multiple="true"
                                    :clear-on-select="false"
                                    :show-labels="false"
                                    @open="valueChanged(resource)"

                                ></multiselect>
                                <select
                                    v-else-if="resource.input_type === 'bool' && ! isMultiSelect(resource)"
                                    v-model="formData[resource.id]"
                                    :name="resource.id"
                                    class="campsite-calendar__form-input-field"
                                    @change="valueChanged(resource)"
                                >
                                    <option />
                                    <option value="true">{{ $t('generic.yes') }}</option>
                                    <option value="false">{{ $t('generic.no') }}</option>
                                </select>
                                <select
                                    v-else-if="resource.input_type === 'choices'"
                                    v-model="formData[resource.id]"
                                    :name="resource.id"
                                    class="campsite-calendar__form-input-field"
                                    @change="valueChanged(resource)"
                                >
                                    <option />
                                    <option
                                        v-for="choice in resource.choices"
                                        :value="choice.key">
                                        {{ choice.key }} : {{ choice.name}}
                                    </option>
                                </select>
                                <input
                                    v-else
                                    v-model="formData[resource.id]"
                                    class="campsite-calendar__form-input-field"
                                    :id="resource.id"
                                    :type="getInputType(resource)"
                                    @input="valueChanged(resource)"
                                    @keypress="inputBinding($event, resource)"
                                >
                            </span>
                            <label
                                class="switch is-success"
                                :class="{'checked': !isInputDisabled(resource)}"
                                @click="toggleInputState(resource)"
                            >
                                <input
                                    type="checkbox"
                                    :name="'btn_' + resource.id"
                                    tabindex="-1"
                                    :checked="!isInputDisabled(resource)"
                                >
                            </label>
                        </li>
                    </template>
                </ul>
            </Modal>
        </template>

        <Preloader
            v-if="!calendarDidLoad"
            :is-local="true"
        />
    </div>
</template>

<script>
    import draggable from 'vuedraggable';
    import fullCalendar from 'vue-fullcalendar';
    import { Api } from '../../../api';
    import { ListCalendar, Modal, CalendarFilters, Preloader } from '../../components';
    import {AppModel} from '../../../models';
    import moment from 'moment';
    import Multiselect from 'vue-multiselect';

    export default {
        name: 'campsite-calendar',
        components: {
            draggable,
            fullCalendar,
            ListCalendar,
            Modal,
            CalendarFilters,
            Preloader,
            Multiselect
        },
        data() {
            return {
                fileDidUpload: true,
                calendarDidLoad: false,
                excelDownloading: false,
                filterExtras: null,
                onlyActive: false,
                datePickerOptions: {
                    minDate: new Date(),
                    // maxDate: new Date(moment().add(18, 'months').valueOf()),
                    maxDate: new Date(AppModel.activeCampground.pricelist_ranges.max),
                },
                selectedDate: {
                    end: new Date(),
                    start: new Date(),
                },
                formData: {},
                resources: null,
                resourceExtras: null,
                campsites: [],
                selectedCampsite: null,
                enabledInputFieldIds: [],
                multiSelectOptions: [
                    {'code': 255, 'label': 'Alle'},
                    {'code': -255, 'label': 'Keine'},
                    {'code': 1, 'label': 'Mo'},
                    {'code': 2, 'label': 'Di'},
                    {'code': 3, 'label': 'Mi'},
                    {'code': 4, 'label': 'Do'},
                    {'code': 5, 'label': 'Fr'},
                    {'code': 6, 'label': 'Sa'},
                    {'code': 7, 'label': 'So'}],
                previousDateEnd: null,
                previousDateStart: null,
                isSubmitPending: false,
            };
        },

        beforeRouteEnter(to, from, next) {
            Api.bootstrap().then(function(response) {
                return Promise.all([response, Api.campsites.fetchCampsites()]);
            }).then(function(response) {
                next((vm) => {
                    vm.campsites = response[1].body;
                });
            });
        },
        async created() {
            await this.fetchResources();
        },
        computed: {
            modalTitle() {
                if(this.selectedCampsite) {
                    return this.selectedCampsite[0].name;
                }

                return '';
            },
            isSaveButtonDisabled() {
                return !this.isSubmitPending && Object.keys(this.formData).length === 0;
            },
            excelIsDownloading() {
                return this.excelDownloading;
            }
        },
        methods: {
            hideCalendarOnTab(event, hideFn) {
                if(event.code.toLowerCase() === 'tab') {
                    setTimeout(() => {
                        hideFn();
                    }, 150);
                }
            },
            setEndDate(date, phase, callback) {
                if(phase) {
                    date = date.target.value;
                    date = moment(date, 'DD.MM.YYYY');
                    date = new Date(date.format());

                    if(date === 'Invalid Date') {
                        return;
                    }

                    this.selectedDate[phase] = date;
                }

                if(date.getTime() > this.selectedDate.end.getTime()) {
                    this.selectedDate.end = date;
                }

                setTimeout(() => {
                    if(callback) {
                        callback();
                    }
                }, 150);
            },
            setTodayDate(selectedDateKey) {
                const date = new Date();
                this.selectedDate[selectedDateKey] = date;
                this.$refs[`${selectedDateKey}Date`].move(date);
            },
            setMaxDate(selectedDateKey) {
                const date = new Date(AppModel.activeCampground.pricelist_ranges.max);
                this.selectedDate[selectedDateKey] = date;
                this.$refs[`${selectedDateKey}Date`].move(date);
            },
            isMultiSelect(resource) {
                return (resource.id.indexOf('_can_arrive') > 0 || resource.id.indexOf('_can_departure') > 0);
            },
            async fetchResources() {
                this.resources = (await Api.resources.fetchResources()).data;

                if(this.resources && this.resources.length <= 0) {
                    return;
                }

                this.resources = this.resources.reduce((newResources, resources) => {
                    const items = [];

                    resources.forEach((resource) => {
                        items.push(...resource.items);
                    });
                    newResources.push(items);

                    return newResources;
                }, []);
            },
            setCampsite(campsite, startDate, endDate) {
                this.selectedCampsite = campsite;
                this.selectedDate.start = new Date(startDate);
                this.selectedDate.end = new Date(endDate);

                this.$refs.modal.show = true;
            },
            resetSelectedCampsite() {
                this.selectedCampsite = null;
                this.formData = { };
                this.selectedDate = {
                    end: new Date(),
                    start: new Date(),
                };
                this.enabledInputFieldIds = [];
            },
            valueChanged(resource) {
                if(this.enabledInputFieldIds.indexOf(resource.id) === -1) {
                    this.toggleInputState(resource);
                }
            },
            getInputType(resource) {
                switch (resource.input_type) {
                case 'bool':
                    return 'checkbox';
                case 'int':
                    return 'number';
                case 'price':
                    return 'number';
                default:
                    return 'text';
                }
            },
            inputBinding(event, resource) {
                const char = event.key;
                const regex = /[.,-]/g;

                if(resource.input_type === 'int') {
                    if(regex.test(char)) {
                        event.preventDefault();
                    }
                }
            },
            isInputDisabled(resource) {
                const index = this.enabledInputFieldIds.indexOf(resource.id);

                return index === -1;
            },
            toggleInputState(resource) {
                const index = this.enabledInputFieldIds.indexOf(resource.id);

                if(index === -1) {
                    this.enabledInputFieldIds.push(resource.id);
                } else {
                    this.enabledInputFieldIds.splice(index, 1);
                    this.$delete(this.formData, resource.id);
                }
            },
            async saveDayPrices() {
                const campsite = this.selectedCampsite[0].id.split(':').pop();
                const start = moment(this.selectedDate.start).format('YYYY-MM-DD');
                const end = moment(this.selectedDate.end).format('YYYY-MM-DD');
                const data = [];
                this.isSubmitPending = true;

                for(const key in this.formData) {
                    data.push({
                        resource: key,
                        value: this.formData[key]
                    });
                }

                const response = await Api.dayPrices.setDayPrices({
                    campsite,
                    start,
                    end,
                    data,
                });

                if(response.data.status === 'okay') {
                    const date = moment(start);
                    this.$refs.calendar.selectedMonth = {
                        month: date.format('MM'),
                        year: date.format('YYYY')
                    };
                    this.$refs.calendar.showFirstHalfOfMonth = parseInt(date.format('DD')) < 16;
                    this.$refs.calendar.$refs.vcal.move(date.format('YYYY-MM-DD'));
                    await this.updateData();
                }

                this.isSubmitPending = false;
            },
            async updateData() {
                await this.$refs.calendar.fetchData();
            },
            collapseAll() {
                this.$refs.calendar.collapseAll();
            },
            fileUploading() {
                this.calendarDidLoad = false;
                this.fileDidUpload = false;
            },
            fileUploaded() {
                this.fileDidUpload = true;
                this.calendarDidLoad = false;
            },
            calendarLoaded(extras) {
                this.calendarDidLoad = true;
                this.resourceExtras = extras;
            },
            excelDownloadStarted() {
                this.excelDownloading = true;
            },
            excelDownloadFinished() {
                this.excelDownloading = false;
            },
            filterOnlyActive(value) {
                this.$refs.calendar.filter_only_active = value;
            }
        },
    };
</script>

<style lang="scss" scoped src="./campsite-calendar.scss" />

<style lang="css">
.modal-card {
    width: 45%;
    min-width: 45%;
    min-width: 45%;
}
</style>



// WEBPACK FOOTER //
// src/views/pages/campsite-calendar/campsite-calendar.vue